import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Button, Spinner, StatusBadge, Textarea } from '@noor/ui';
import { cn } from '@noor/lib/cn';
import {
  BROADCAST_STATUS_LABEL_KEYS,
  BROADCAST_STATUS_TONES,
  broadcastDownloadName,
  buildBroadcastExpiryLabel,
  isBroadcastExpired,
  type BroadcastExpiryTone,
  type VendorBroadcast,
} from '@/entities/vendor-broadcast';
import { useDownloadBroadcastFile } from '../model/use-download-broadcast-file';
import { useRespondToBroadcast } from '../model/use-respond-to-broadcast';
import { BroadcastFileIcon } from './broadcast-file-icon';

/** Countdown pill tones — red under an hour, amber under a day, green beyond. */
const EXPIRY_TONES = {
  urgent: 'danger',
  warning: 'pending',
  safe: 'success',
} as const satisfies Record<BroadcastExpiryTone, 'danger' | 'pending' | 'success'>;

/**
 * One broadcast notification — the old panel's `BroadcastCard`, ported.
 * Title + status pill, message, optional attachment, date + live countdown,
 * and the Accept/Reject actions on actionable, unexpired items.
 */
export function BroadcastCard({
  item,
  onResponded,
}: {
  item: VendorBroadcast;
  onResponded?: () => void;
}) {
  const { t } = useTranslation('common');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [now, setNow] = useState(() => Date.now());

  // The countdown ticks each second while an expiry is set — the source's
  // exact behavior, so the pill flips to expired without a refetch.
  useEffect(() => {
    if (!item.expires_at) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [item.expires_at]);

  const respond = useRespondToBroadcast();

  // Runs on EITHER outcome (the source's mutation-level onSuccess): reset the
  // reject form and, inside the fresh-items dialog, dismiss it.
  const handleResponded = () => {
    setRejectOpen(false);
    setReason('');
    onResponded?.();
  };

  const clientExpired = isBroadcastExpired(item.expires_at, now);
  const isExpired = item.status === 'expired' || clientExpired;
  const canRespond =
    (item.status === 'fresh' || item.status === 'viewed') &&
    item.with_action &&
    !clientExpired;
  const fileLabel = t('broadcasts.file');
  const fileName = item.file_name ?? fileLabel;
  const expiry =
    !isExpired && item.expires_at
      ? buildBroadcastExpiryLabel(item.expires_at, now, (key, vars) => t(key, vars ?? {}))
      : null;

  return (
    <article
      className={cn(
        'rounded-3xl bg-card p-4 sm:p-6',
        isExpired && 'bg-muted ring-1 ring-status-danger/30 ring-inset',
      )}
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <h3 className="text-lg leading-6 font-semibold tracking-tight break-words text-foreground sm:text-xl">
          {item.title}
        </h3>
        <StatusBadge tone={BROADCAST_STATUS_TONES[item.status]} className="shrink-0">
          {t(BROADCAST_STATUS_LABEL_KEYS[item.status])}
        </StatusBadge>
      </div>

      <p className="mb-4 text-[15px] leading-[22px] font-medium break-words whitespace-pre-wrap text-muted-foreground">
        {item.message}
      </p>

      {item.file_path && (
        <BroadcastAttachment
          path={item.file_path}
          fileName={item.file_name}
          label={fileName}
          fallbackLabel={fileLabel}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[13px] leading-4 font-medium text-muted-foreground">
          {dayjs(item.created_at).format('DD.MM.YYYY')}
        </span>
        {expiry && (
          <StatusBadge tone={EXPIRY_TONES[expiry.tone]}>{expiry.text}</StatusBadge>
        )}
      </div>

      {canRespond && !rejectOpen && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button
            size="sm"
            disabled={respond.isPending}
            onClick={() =>
              respond.mutate(
                { id: item.id, status: 'accepted' },
                { onSuccess: handleResponded },
              )
            }
          >
            {respond.isPending ? <Spinner size="xs" /> : t('broadcasts.accept')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={respond.isPending}
            onClick={() => setRejectOpen(true)}
            className="border-status-danger/30 text-status-danger hover:border-status-danger/40 hover:bg-status-danger/15 hover:text-status-danger"
          >
            {t('broadcasts.reject')}
          </Button>
        </div>
      )}

      {rejectOpen && (
        <div className="mt-4">
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('broadcasts.reject_reason_placeholder')}
            rows={3}
            className="mb-3"
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Button
              size="sm"
              variant="destructive"
              disabled={respond.isPending}
              onClick={() => {
                respond.mutate(
                  { id: item.id, status: 'rejected', rejectReason: reason },
                  { onSuccess: handleResponded },
                );
              }}
            >
              {respond.isPending ? <Spinner size="xs" /> : t('broadcasts.confirm_reject')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setRejectOpen(false);
                setReason('');
              }}
            >
              {t('broadcasts.cancel')}
            </Button>
          </div>
        </div>
      )}
    </article>
  );
}

/**
 * The attachment chip. Split out so `path` narrows to `string` — the field is
 * nullable on the wire and the card only renders this when it is set.
 */
function BroadcastAttachment({
  path,
  fileName,
  label,
  fallbackLabel,
}: {
  path: string;
  fileName: string | null;
  label: string;
  fallbackLabel: string;
}) {
  const download = useDownloadBroadcastFile();

  return (
    <button
      type="button"
      disabled={download.isPending}
      onClick={() =>
        download.mutate({
          path,
          filename: broadcastDownloadName(path, fileName, fallbackLabel),
        })
      }
      className="hover:bg-secondary-hover mb-4 inline-flex max-w-full cursor-pointer items-center gap-2 rounded-2xl border border-border bg-muted px-3 py-2 transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-default"
    >
      {download.isPending ? (
        <Spinner size="sm" />
      ) : (
        <BroadcastFileIcon name={fileName ?? path} />
      )}
      <span className="truncate text-sm font-semibold text-foreground">{label}</span>
    </button>
  );
}
